import { __assign } from "tslib";
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useEffect, useRef } from "react";
import clsx from "clsx";
import { isLinkItem } from "../utils/utils";
import styles from "./styles.css.js";
import Tooltip from "../tooltip";
import { getItemTarget } from "../utils/utils";
import useHiddenDescription from "../utils/use-hidden-description";
import InternalIcon from "../../icon/internal";
import { useDropdownContext } from "../../internal/components/dropdown/context";
import { getMenuItemProps } from "../utils/menu-item";
var ItemElement = function(_a) {
    var _b;
    var item = _a.item, disabled = _a.disabled, onItemActivate = _a.onItemActivate, highlighted = _a.highlighted, highlightItem = _a.highlightItem, _c = _a.first, first = _c === void 0 ? false : _c, last = _a.last, hasCategoryHeader = _a.hasCategoryHeader, _d = _a.isKeyboardHighlighted, isKeyboardHighlighted = _d === void 0 ? false : _d, _e = _a.variant, variant = _e === void 0 ? "normal" : _e;
    var isLink = isLinkItem(item);
    var onClick = function(event) {
        // Stop propagation to parent node and handle event exclusively in here. This ensures
        // that no group will interfere with the default behavior of links
        event.stopPropagation();
        if (!isLink) {
            event.preventDefault();
        }
        if (!disabled) {
            onItemActivate(item, event);
        }
    };
    var onHover = function(event) {
        event.preventDefault();
        highlightItem(item);
    };
    return /*#__PURE__*/ React.createElement("li", {
        className: clsx(styles["item-element"], styles["variant-".concat(variant)], (_b = {}, _b[styles.highlighted] = highlighted, _b[styles.disabled] = disabled, _b[styles.first] = first, _b[styles.last] = last, _b[styles["has-category-header"]] = hasCategoryHeader, _b[styles["is-focused"]] = isKeyboardHighlighted, _b)),
        role: "presentation",
        "data-testid": item.id,
        "data-description": item.description,
        onClick: onClick,
        onMouseEnter: onHover,
        onTouchStart: onHover
    }, /*#__PURE__*/ React.createElement(MenuItem, {
        item: item,
        disabled: disabled,
        highlighted: highlighted
    }));
};
function MenuItem(_a) {
    var item = _a.item, disabled = _a.disabled, highlighted = _a.highlighted;
    var menuItemRef = useRef(null);
    useEffect(function() {
        if (highlighted && menuItemRef.current) {
            menuItemRef.current.focus();
        }
    }, [
        highlighted
    ]);
    var isDisabledWithReason = disabled && item.disabledReason;
    var _b = useHiddenDescription(item.disabledReason), targetProps = _b.targetProps, descriptionEl = _b.descriptionEl;
    var menuItemProps = __assign(__assign({
        className: styles["menu-item"],
        ref: menuItemRef,
        // We are using the roving tabindex technique to manage the focus state of the dropdown.
        // The current element will always have tabindex=0 which means that it can be tabbed to,
        // while all other items have tabindex=-1 so we can focus them when necessary.
        tabIndex: highlighted ? 0 : -1
    }, getMenuItemProps({
        disabled: disabled
    })), isDisabledWithReason ? targetProps : {});
    var menuItem = isLinkItem(item) ? /*#__PURE__*/ React.createElement("a", __assign({}, menuItemProps, {
        href: !disabled ? item.href : undefined,
        target: getItemTarget(item),
        rel: item.external ? "noopener noreferrer" : undefined
    }), /*#__PURE__*/ React.createElement(MenuItemContent, {
        item: item,
        disabled: disabled
    })) : /*#__PURE__*/ React.createElement("span", __assign({}, menuItemProps), /*#__PURE__*/ React.createElement(MenuItemContent, {
        item: item,
        disabled: disabled
    }));
    var position = useDropdownContext().position;
    var tooltipPosition = position === "bottom-left" || position === "top-left" ? "left" : "right";
    return isDisabledWithReason ? /*#__PURE__*/ React.createElement(Tooltip, {
        content: item.disabledReason,
        position: tooltipPosition
    }, menuItem, descriptionEl) : menuItem;
}
var MenuItemContent = function(_a) {
    var item = _a.item, disabled = _a.disabled;
    var hasIcon = !!(item.iconName || item.iconUrl || item.iconSvg);
    var hasExternal = isLinkItem(item) && item.external;
    return /*#__PURE__*/ React.createElement(React.Fragment, null, hasIcon && /*#__PURE__*/ React.createElement(MenuItemIcon, {
        name: item.iconName,
        url: item.iconUrl,
        svg: item.iconSvg,
        alt: item.iconAlt
    }), " ", item.text, " ", hasExternal && /*#__PURE__*/ React.createElement(ExternalIcon, {
        disabled: disabled,
        ariaLabel: item.externalIconAriaLabel
    }));
};
var MenuItemIcon = function(props) {
    return /*#__PURE__*/ React.createElement("span", {
        className: styles.icon
    }, /*#__PURE__*/ React.createElement(InternalIcon, __assign({}, props)));
};
var ExternalIcon = function(_a) {
    var disabled = _a.disabled, ariaLabel = _a.ariaLabel;
    var icon = /*#__PURE__*/ React.createElement(InternalIcon, {
        variant: disabled ? "disabled" : "normal",
        name: "external"
    });
    return /*#__PURE__*/ React.createElement("span", {
        className: styles["external-icon"],
        role: ariaLabel ? "img" : undefined,
        "aria-label": ariaLabel
    }, icon);
};
export default ItemElement; //# sourceMappingURL=index.js.map
